/**
 * Date Validation Utility
 * 
 * Provides comprehensive date validation functions for the senior care system.
 * Handles leap years, calendar validation, and business logic rules.
 */

/**
 * Validates if a date string represents a valid calendar date
 * Handles leap years and correct days per month
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateCalendarDate(dateString) {
  if (!dateString || !dateString.trim()) {
    return { isValid: false, error: "Date is required" };
  }

  // Parse the date components
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return { isValid: false, error: "Invalid date format. Use YYYY-MM-DD" };
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate components are numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return { isValid: false, error: "Invalid date values" };
  }

  // Validate year range (1900 to current year + 1)
  const currentYear = new Date().getFullYear();
  if (year < 1900 || year > currentYear + 1) {
    return { isValid: false, error: `Year must be between 1900 and ${currentYear}` };
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, error: "Month must be between 1 and 12" };
  }

  // Days in each month (non-leap year)
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Check for leap year
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  if (isLeapYear) {
    daysInMonth[1] = 29; // February has 29 days in leap year
  }

  // Validate day
  const maxDays = daysInMonth[month - 1];
  if (day < 1 || day > maxDays) {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return { 
      isValid: false, 
      error: `${monthNames[month - 1]} ${year} has only ${maxDays} days` 
    };
  }

  // Create date object and verify it matches input (catches invalid dates like Feb 30)
  const dateObj = new Date(year, month - 1, day);
  if (dateObj.getFullYear() !== year || 
      dateObj.getMonth() !== month - 1 || 
      dateObj.getDate() !== day) {
    return { isValid: false, error: "Invalid calendar date" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates Date of Birth with business rules
 * - Must be valid calendar date
 * - Cannot be in the future
 * - Must be after 1900-01-01
 * - Cannot result in age > 125 years
 * 
 * @param {string} dobString - Date of birth in YYYY-MM-DD format
 * @param {number} minAge - Minimum age required (default: 0)
 * @returns {Object} { isValid: boolean, error: string|null, age: number|null }
 */
export function validateDateOfBirth(dobString, minAge = 0) {
  // First validate calendar date
  const calendarCheck = validateCalendarDate(dobString);
  if (!calendarCheck.isValid) {
    return { isValid: false, error: calendarCheck.error, age: null };
  }

  const [year, month, day] = dobString.split('-').map(num => parseInt(num, 10));
  const birthDate = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if date is in the future
  if (birthDate > today) {
    return { isValid: false, error: "Date of birth cannot be in the future", age: null };
  }

  // Check if date is before 1900
  const minDate = new Date(1900, 0, 1);
  if (birthDate < minDate) {
    return { isValid: false, error: "Date of birth must be after January 1, 1900", age: null };
  }

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  // Adjust age if birthday hasn't occurred this year yet
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // Check maximum age (125 years)
  if (age > 125) {
    return { isValid: false, error: "Age cannot exceed 125 years", age: null };
  }

  // Check minimum age if specified
  if (minAge > 0 && age < minAge) {
    return { 
      isValid: false, 
      error: `Must be at least ${minAge} years old. Current age: ${age} years`, 
      age 
    };
  }

  return { isValid: true, error: null, age };
}

/**
 * Calculates age from date of birth
 * 
 * @param {string} dobString - Date of birth in YYYY-MM-DD format
 * @returns {number|null} Age in years, or null if invalid date
 */
export function calculateAge(dobString) {
  const validation = validateDateOfBirth(dobString);
  return validation.isValid ? validation.age : null;
}

/**
 * Validates admission date with business rules
 * - Must be valid calendar date
 * - Cannot be in the future
 * - Cannot be before date of birth
 * - Person must be at least minAge years old at admission
 * 
 * @param {string} admissionDateString - Admission date in YYYY-MM-DD format
 * @param {string} dobString - Date of birth in YYYY-MM-DD format
 * @param {number} minAge - Minimum age at admission (default: 0)
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateAdmissionDate(admissionDateString, dobString, minAge = 0) {
  if (!admissionDateString || !admissionDateString.trim()) {
    // Admission date is optional in some cases
    return { isValid: true, error: null };
  }

  // Validate calendar date
  const calendarCheck = validateCalendarDate(admissionDateString);
  if (!calendarCheck.isValid) {
    return { isValid: false, error: calendarCheck.error };
  }

  const [admYear, admMonth, admDay] = admissionDateString.split('-').map(num => parseInt(num, 10));
  const admissionDate = new Date(admYear, admMonth - 1, admDay);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Check if admission date is in the future
  if (admissionDate > today) {
    return { isValid: false, error: "Admission date cannot be in the future" };
  }

  // If DOB is provided, validate against it
  if (dobString && dobString.trim()) {
    const dobValidation = validateDateOfBirth(dobString);
    if (!dobValidation.isValid) {
      return { isValid: false, error: "Invalid date of birth" };
    }

    const [dobYear, dobMonth, dobDay] = dobString.split('-').map(num => parseInt(num, 10));
    const birthDate = new Date(dobYear, dobMonth - 1, dobDay);

    // Admission date cannot be before birth date
    if (admissionDate < birthDate) {
      return { 
        isValid: false, 
        error: "Admission date cannot be before date of birth" 
      };
    }

    // Calculate age at admission
    let ageAtAdmission = admissionDate.getFullYear() - birthDate.getFullYear();
    const monthDiff = admissionDate.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && admissionDate.getDate() < birthDate.getDate())) {
      ageAtAdmission--;
    }

    // Check minimum age at admission
    if (minAge > 0 && ageAtAdmission < minAge) {
      return { 
        isValid: false, 
        error: `Person must be at least ${minAge} years old at admission. Age at admission was ${ageAtAdmission} years` 
      };
    }
  }

  return { isValid: true, error: null };
}

/**
 * Validates a calendar date with custom year range
 * Used for dates that can be far in the future (like certification expiry dates)
 * 
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {number} minYear - Minimum year allowed (default: 1900)
 * @param {number} maxYear - Maximum year allowed (default: 2100)
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateCalendarDateWithRange(dateString, minYear = 1900, maxYear = 2100) {
  if (!dateString || !dateString.trim()) {
    return { isValid: false, error: "Date is required" };
  }

  // Parse the date components
  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return { isValid: false, error: "Invalid date format. Use YYYY-MM-DD" };
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);

  // Validate components are numbers
  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return { isValid: false, error: "Invalid date values" };
  }

  // Validate year range
  if (year < minYear || year > maxYear) {
    return { isValid: false, error: `Year must be between ${minYear} and ${maxYear}` };
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, error: "Month must be between 1 and 12" };
  }

  // Days in each month (non-leap year)
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

  // Check for leap year
  const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
  if (isLeapYear) {
    daysInMonth[1] = 29; // February has 29 days in leap year
  }

  // Validate day
  const maxDays = daysInMonth[month - 1];
  if (day < 1 || day > maxDays) {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return { 
      isValid: false, 
      error: `${monthNames[month - 1]} ${year} has only ${maxDays} days` 
    };
  }

  // Create date object and verify it matches input (catches invalid dates like Feb 30)
  const dateObj = new Date(year, month - 1, day);
  if (dateObj.getFullYear() !== year || 
      dateObj.getMonth() !== month - 1 || 
      dateObj.getDate() !== day) {
    return { isValid: false, error: "Invalid calendar date" };
  }

  return { isValid: true, error: null };
}

/**
 * Validates certification dates
 * - Issue date must be valid calendar date
 * - Expiry date must be valid calendar date (can be far in the future)
 * - Expiry date must be after issue date
 * 
 * @param {string} issueDateString - Issue date in YYYY-MM-DD format
 * @param {string} expiryDateString - Expiry date in YYYY-MM-DD format
 * @returns {Object} { isValid: boolean, error: string|null }
 */
export function validateCertificationDates(issueDateString, expiryDateString) {
  // Validate issue date if provided (use standard validation - past dates)
  if (issueDateString && issueDateString.trim()) {
    const issueCheck = validateCalendarDate(issueDateString);
    if (!issueCheck.isValid) {
      return { isValid: false, error: `Issue date: ${issueCheck.error}` };
    }
  }

  // Validate expiry date if provided (allow far future dates up to year 2100)
  if (expiryDateString && expiryDateString.trim()) {
    const expiryCheck = validateCalendarDateWithRange(expiryDateString, 1900, 2100);
    if (!expiryCheck.isValid) {
      return { isValid: false, error: `Expiry date: ${expiryCheck.error}` };
    }

    // If both dates provided, expiry must be after issue
    if (issueDateString && issueDateString.trim()) {
      const issueDate = new Date(issueDateString);
      const expiryDate = new Date(expiryDateString);
      
      if (expiryDate <= issueDate) {
        return { 
          isValid: false, 
          error: "Expiry date must be after issue date" 
        };
      }
    }
  }

  return { isValid: true, error: null };
}
